/**
 * NudgesOptOutPage — the "Stop these messages" link under every Wright stop
 * DM and decisions digest that YAGNI sends by Slack or email.
 *
 * URL contract: /nudges/opt-out?t=<signed token>
 *
 * The page posts the token (POST /api/work-nudges/opt-out) instead of the
 * link doing it, because mail scanners open links. Idempotent. No app chrome:
 * reachable signed out, like ActivationOptOutPage.
 */

import { useEffect, useMemo, useState } from "react";
import { Card } from "../ui/card.js";

type Status = "loading" | "missing_token" | "success" | "invalid" | "error";

export function NudgesOptOutPage() {
  const token = useMemo(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("t");
  }, []);

  const [status, setStatus] = useState<Status>(token ? "loading" : "missing_token");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/work-nudges/opt-out", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ token }),
          credentials: "omit",
        });
        if (cancelled) return;
        if (res.status === 204) setStatus("success");
        else if (res.status === 400) setStatus("invalid");
        else setStatus("error");
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen w-full [background:var(--canvas-field)] flex items-center justify-center p-6">
      <Card className="max-w-md w-full p-8 space-y-4">
        <h1 className="text-xl font-semibold">
          {status === "success" ? "You won't get these messages anymore" : "YAGNI messages"}
        </h1>
        {status === "loading" && (
          <p className="text-sm text-text-secondary">One moment, turning these messages off.</p>
        )}
        {status === "success" && (
          <p className="text-sm">
            YAGNI will not email or Slack you about stopped builds or waiting decisions. You still
            see them in YAGNI under Waiting on you.
          </p>
        )}
        {status === "missing_token" && (
          <p className="text-sm text-text-secondary">
            This link is missing its token. Open it from the email or Slack message directly.
          </p>
        )}
        {status === "invalid" && (
          <p className="text-sm text-text-secondary">This link is no longer valid.</p>
        )}
        {status === "error" && (
          <p className="text-sm text-text-secondary">
            Something went wrong. Try the link again in a minute.
          </p>
        )}
      </Card>
    </div>
  );
}
